/**
 * NotificationBell — bell icon with unread badge + dropdown panel.
 *
 * Placed in the top navigation for both admin and contestant layouts.
 * Click opens a dropdown showing recent notifications.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Bell, MessageSquare, Heart, Calendar, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotificationCenter, type NotificationItem } from '@/hooks/useNotificationCenter';

function iconForType(type: string) {
  switch (type) {
    case 'NEW_MESSAGE':
    case 'NEW_REPLY':
      return MessageSquare;
    case 'NEW_REACTION':
      return Heart;
    case 'NEW_CALENDAR_EVENT':
      return Calendar;
    default:
      return CheckCircle;
  }
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

export function NotificationBell(): React.ReactElement {
  const { isAuthenticated, role } = useAuth();
  const { notifications, unreadCount, markRead, markAllRead } = useNotificationCenter(isAuthenticated);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  function handleTap(item: NotificationItem) {
    markRead(item.id);
    setOpen(false);
    try {
      const target = JSON.parse(item.deepLinkTarget);
      if (target.screen === 'Channel' && target.params?.cid) {
        const basePath = role === 'admin' || role === 'poweruser' ? '/admin/messages' : '/portal/messages';
        navigate(`${basePath}?channel=${target.params.cid}${target.params.messageId ? `&message=${target.params.messageId}` : ''}`);
      } else if (target.screen === 'Calendar') {
        navigate(role === 'admin' || role === 'poweruser' ? '/admin/calendar' : '/portal/calendar');
      } else if (target.screen === 'Profile') {
        navigate('/portal/profile');
      }
    } catch { /* ignore */ }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => {
          const willOpen = !open;
          setOpen(willOpen);
          // Mark all notifications as read when the drawer opens —
          // the user has acknowledged them by clicking the bell.
          if (willOpen && unreadCount > 0) {
            markAllRead();
          }
        }}
        className="relative p-2 touch-target rounded-full hover:bg-accent transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {unreadCount > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-white text-[10px] font-bold px-1 border-2 border-background">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-[400px] overflow-y-auto bg-card border border-border shadow-prominent rounded-lg z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="font-display text-lg text-foreground">Notifications</h3>
            {unreadCount > 0 ? (
              <button
                onClick={() => markAllRead()}
                className="font-sans text-[9px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors touch-target"
              >
                Mark all read
              </button>
            ) : null}
          </div>

          {notifications.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Bell className="w-6 h-6 text-muted-foreground/50 mx-auto mb-2" />
              <p className="font-sans text-xs text-muted-foreground">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((item) => {
                const Icon = iconForType(item.type);
                const isUnread = !item.readAt;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleTap(item)}
                    className={`w-full text-left px-4 py-3 min-h-[var(--touch-target-min)] flex items-start gap-3 hover:bg-accent/50 transition-colors ${isUnread ? 'border-l-2 border-l-primary' : ''}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className={`w-4 h-4 ${isUnread ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-sm font-semibold text-foreground truncate">
                        {item.actorName}
                      </p>
                      <p className="font-sans text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {item.preview}
                      </p>
                    </div>
                    <span className="font-sans text-[10px] text-muted-foreground shrink-0 mt-0.5">
                      {relativeTime(item.createdAt)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
